import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useApi from '../../Api';
import zxcvbn from 'zxcvbn';
import '../../styles/components/user/Signup.css';
import { validateTextInput } from '../common/TextValidator';
import Popup from '../common/popup/Popup';
import { FaUser, FaEnvelope, FaLock } from 'react-icons/fa';
import TermsDisclaimer from '../common/TermsDisclaimer';

const Signup = ({ show, handleClose, switchToLogin }) => {
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [usernameCheckError, setUsernameCheckError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [passwordScore, setPasswordScore] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const apiClient = useApi();
  const debounceRef = useRef(null);

  useEffect(() => {
    if (username.trim().length >= 3) {
      const validation = validateTextInput(username, {
        field_name: "Username",
        maxLength: 20,
        minLength: 3,
        allowSpecialChars: true
      });
      if (!validation.isValid) {
        setUsernameAvailable(false);
        setUsernameCheckError(validation.errors.join(' '));
        return;
      } else {
        setUsernameCheckError('');
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const response = await apiClient.post('/check-username-available', { username: username.trim() });
          if (!response.data.available) {
            setUsernameAvailable(false);
            setUsernameCheckError('This username is not available.');
          } else {
            setUsernameAvailable(true);
            setUsernameCheckError('');
          }
        } catch (err) {
          setUsernameAvailable(true);
          setUsernameCheckError('Error checking username availability.');
        }
      }, 500);
    } else {
      setUsernameAvailable(true);
      setUsernameCheckError('');
    }
  }, [username, apiClient]);

  const validateInputs = () => {
    let valid = true;
    let errorMessages = [];
    const usernameValidation = validateTextInput(username, {
      field_name: "Username",
      maxLength: 20,
      minLength: 3,
      allowSpecialChars: true
    });
    if (!usernameValidation.isValid) {
      valid = false;
      errorMessages.push(...usernameValidation.errors.map(err => `Username: ${err}`));
    }
    if (!usernameAvailable) {
      valid = false;
      errorMessages.push('This username is not available.');
    }
    const emailValidation = validateTextInput(email, {
      field_name: "Email",
      maxLength: 255,
      allowSpecialChars: true,
    });
    if (!emailValidation.isValid) {
      valid = false;
      errorMessages.push(...emailValidation.errors.map(err => `Email: ${err}`));
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      valid = false;
      errorMessages.push(`Password: ${passwordError}`);
    }
    if (password !== confirmPassword) {
      valid = false;
      errorMessages.push('Confirm Password: Passwords do not match.');
    }
    if (!valid) {
      setError(errorMessages.join(' '));
    } else {
      setError('');
    }
    return valid;
  };

  const validatePassword = (pwd) => {
    const minLength = 6;
    if (pwd.length < minLength) {
      return `Password must be at least ${minLength} characters long.`;
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    if (error) setError('');
    const evaluation = zxcvbn(pwd);
    setPasswordScore(evaluation.score);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await apiClient.post('/signup', {
        email,
        password,
        username: username.trim()
      });
      if (response.status === 201) {
        setLoading(false);
        setCountdown(5);
        setMessage('Please check your email to verify your account. Closing in 5 seconds.');
      }
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.data && err.response.data.detail) {
        if (Array.isArray(err.response.data.detail)) {
          const errorMsgs = err.response.data.detail.map(
            (error) => `${error.msg}`
          ).join(' ');
          setError(errorMsgs);
        } else if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else {
          setError('Failed to create account. Please try again.');
        }
      } else {
        setError('Failed to create account. Please try again.');
      }
    }
  };

  const closePopup = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
    setPasswordScore(0);
    setCountdown(0);
    handleClose && handleClose();
  };

  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          const next = prev - 1;
          if (next > 0) {
            setMessage(`Please check your email to verify your account. Closing in ${next} seconds.`);
          } else {
            closePopup();
          }
          return next;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown]);

  useEffect(() => {
    if (!show) {
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setMessage('');
      setPasswordScore(0);
      setCountdown(0);
      setLoading(false);
      setUsernameCheckError('');
    }
  }, [show]);

  const PasswordStrengthBar = ({ score }) => {
    const getColor = () => {
      switch (score) {
        case 0:
          return 'red';
        case 1:
          return 'orange';
        case 2:
          return 'yellow';
        case 3:
          return 'lightgreen';
        case 4:
          return 'green';
        default:
          return 'grey';
      }
    };
    const getLabel = () => {
      switch (score) {
        case 0:
          return 'Very Weak';
        case 1:
          return 'Weak';
        case 2:
          return 'Fair';
        case 3:
          return 'Good';
        case 4:
          return 'Strong';
        default:
          return '';
      }
    };
    return (
      <div className="password-strength-bar mt-2">
        <div
          style={{
            width: `${(score + 1) * 20}%`,
            height: '5px',
            backgroundColor: getColor(),
            transition: 'width 0.3s'
          }}
        ></div>
        <small style={{ color: getColor() }}>{getLabel()}</small>
      </div>
    );
  };

  if (!show) return null;

  return (
    <Popup
      title="Create an Account"
      onCancel={closePopup}
      className="popup-modal signup-modal"
      footer={
        <p className="w-100 text-center mt-3">
          Already have an account?{' '}
          <span onClick={switchToLogin} className="login-link" style={{ cursor: 'pointer' }}>
            Sign in here
          </span>
        </p>
      }
    >
      <Form onSubmit={handleSignup}>
        <Form.Group controlId="formUsername" className="mb-3">
          <InputGroup>
            <InputGroup.Text className="input-icon"><FaUser /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => { setUsername(e.target.value); if (error) setError(''); }}
              required
              maxLength="20"
              className="form-input"
              disabled={loading}
            />
          </InputGroup>
          {usernameCheckError && (
            <Form.Text className="text-danger">{usernameCheckError}</Form.Text>
          )}
        </Form.Group>
        <Form.Group controlId="formEmail" className="mb-3">
          <InputGroup>
            <InputGroup.Text className="input-icon"><FaEnvelope /></InputGroup.Text>
            <Form.Control
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              required
              className="form-input"
              disabled={loading}
            />
          </InputGroup>
        </Form.Group>
        <Form.Group controlId="formPassword" className="mb-3">
          <InputGroup>
            <InputGroup.Text className="input-icon"><FaLock /></InputGroup.Text>
            <Form.Control
              type="password"
              placeholder="Password"
              value={password}
              onChange={handlePasswordChange}
              required
              className="form-input"
              disabled={loading}
            />
          </InputGroup>
          <PasswordStrengthBar score={passwordScore} />
        </Form.Group>
        <Form.Group controlId="formConfirmPassword" className="mb-3">
          <InputGroup>
            <InputGroup.Text className="input-icon"><FaLock /></InputGroup.Text>
            <Form.Control
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(''); }}
              required
              className="form-input"
              disabled={loading}
            />
          </InputGroup>
        </Form.Group>
        {error && <Alert variant="danger" className="text-center mt-3">{error}</Alert>}
        {message ? (
          <div className="d-flex justify-content-center mt-3">
            <Alert variant="success" className="text-center w-100" style={{ maxWidth: '300px' }}>
              {message}
            </Alert>
          </div>
        ) : (
          <div className="d-grid gap-2 mt-3">
            <Button className="btn-neutral" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Signing up...
                </>
              ) : (
                'Sign Up'
              )}
            </Button>
          </div>
        )}
      </Form>
      <TermsDisclaimer />
    </Popup>
  );
};

export default Signup;
